import { createApi } from '@reduxjs/toolkit/query/react';
import { GetApprovalResponse } from '../../types/approvals';
import { ApprovalsData } from '../../types/approvals';
import { baseApiQuery } from '../basequery';
import { PaginationInfo } from '../../services/FormService';
import { ReviewerSectionData } from '../../types/reviewer';
import { qualityAndRiskApi } from './qualityandrisk.api';
import { formsApi } from './forms.api';

export const approvalsApi = createApi({
  reducerPath: 'approvalsApi',
  baseQuery: baseApiQuery,
  tagTypes: ['ApprovalsID', 'ReviewerSection'],
  endpoints: (builder) => ({
    /**
     * Fetches approvals with optional filters, pagination, and search
     * @param params - { data: stringified JSON of filters, pagination, etc. }
     */
    getApprovals: builder.query<
      { data: { approvals: ApprovalsData[]; pagination: PaginationInfo } },
      { params: Record<string, unknown> }
    >({
      query: ({ params }) => ({
        url: `/approvals`,
        method: 'GET',
        params,
      }),
    }),

    /**
     * Fetches a specific approval form by ID
     * @param id - The ID of the approval form
     */
    getMyApproval: builder.query<GetApprovalResponse, { id: number }>({
      query: ({ id }) => ({ url: `/approvals/${id}`, method: 'GET' }),
      providesTags: (_result, _error, { id }) => [{ type: 'ApprovalsID', id }],
    }),

    /**
     * Fetches reviewer section data for a form
     */
    getReviewerData: builder.query<{ data: ReviewerSectionData }, { id: number }>({
      query: ({ id }) => ({
        url: `/approvals/${id}/reviewer-data`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'ReviewerSection', id }],
    }),

    /**
     * Submits reviewer section and updates form status
     */
    submitReviewerSection: builder.mutation<
      {
        data: {
          id: number;
          status: string;
          emailNotificationSent?: boolean;
        };
      },
      {
        id: number;
        data: {
          isConflictIdentified: boolean;
          isProceedWithProject?: boolean;
          reviewerComments?: string | null;
          qualityRiskComments?: string | null;
        };
      }
    >({
      query: ({ id, data }) => ({
        url: `/approvals/${id}/decision`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ReviewerSection', id },
        { type: 'ApprovalsID', id },
      ],
      onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(qualityAndRiskApi.util.invalidateTags([{ type: 'QualityAndRiskID', id }]));
          dispatch(formsApi.util.invalidateTags([{ type: 'MyFormsID', id }]));
        } catch {
          // Ignore rejected mutation results here so failed PATCH requests
          // do not cause unhandled promise rejections or cross-slice invalidations.
        }
      },
    }),
    saveReviewerSection: builder.mutation<
      {
        data: {
          id: number;
          status: string | null;
          emailNotificationSent?: boolean;
        };
      },
      {
        id: number;
        data: {
          isConflictIdentified?: boolean;
          isProceedWithProject?: boolean | null;
          reviewerComments?: string | null;
          qualityRiskComments?: string | null;
        };
      }
    >({
      query: ({ id, data }) => ({
        url: `/approvals/${id}/reviewer-save`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ReviewerSection', id },
        { type: 'ApprovalsID', id },
      ],
      onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(qualityAndRiskApi.util.invalidateTags([{ type: 'QualityAndRiskID', id }]));
          dispatch(formsApi.util.invalidateTags([{ type: 'MyFormsID', id }]));
        } catch {
          // Ignore rejected mutation results here so failed PATCH requests
          // do not cause unhandled promise rejections or cross-slice invalidations.
        }
      },
    }),
  }),
});

export const {
  useLazyGetApprovalsQuery,
  useGetMyApprovalQuery,
  useGetReviewerDataQuery,
  useLazyGetReviewerDataQuery,
  useSaveReviewerSectionMutation,
  useSubmitReviewerSectionMutation,
} = approvalsApi;
